import { Component, input, output, signal, computed, effect, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { CardViewConfig, ViewModeConfig, ViewState } from '../types';
import { GenericCardViewComponent } from '../generic-card-view';
import { CardViewControlsComponent } from '../card-view-controls';

/**
 * Storage service interface
 */
interface StorageService {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * High-level wrapper component that manages view state and persistence
 */
@Component({
  selector: 'zag-card-view-page-wrapper',
  imports: [GenericCardViewComponent, CardViewControlsComponent],
  templateUrl: './card-view-page-wrapper.component.html',
  styleUrls: ['./card-view-page-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardViewPageWrapperComponent<T = unknown> implements OnInit {
  /**
   * Array of data items to display
   */
  data = input.required<T[]>();

  /**
   * Card view configuration
   */
  cardConfig = input.required<CardViewConfig<T>>();

  /**
   * View mode configuration (optional)
   */
  viewModeConfig = input<ViewModeConfig>();

  /**
   * Default view mode ID
   */
  defaultViewMode = input<string>('card');

  /**
   * Default page size
   */
  defaultPageSize = input<number>(10);

  /**
   * Default sort by ID
   */
  defaultSortBy = input<string>();

  /**
   * Loading state
   */
  loading = input<boolean>(false);

  /**
   * Storage service for persisting state (optional)
   */
  storage = input<StorageService | undefined>();

  /**
   * Emitted when a card is clicked
   */
  cardClick = output<T>();

  /**
   * Current view state
   */
  viewState = signal<ViewState>({
    viewModeId: this.defaultViewMode(),
    sortById: this.defaultSortBy(),
    pageSize: this.defaultPageSize(),
  });

  /**
   * Whether the current view mode is a card mode
   */
  isCardMode = computed(() => {
    const config = this.viewModeConfig();
    if (!config) return true;

    const currentMode = config.modes.find(m => m.id === this.viewState().viewModeId);
    return currentMode?.isCardMode ?? true;
  });

  /**
   * Storage keys
   */
  private get storageKeys() {
    const prefix = this.viewModeConfig()?.storageKeyPrefix || 'card-view';
    return {
      viewMode: `${prefix}-view-mode`,
      sortBy: `${prefix}-sort-by`,
      pageSize: `${prefix}-page-size`,
    };
  }

  constructor() {
    // Save state to storage whenever it changes
    effect(() => {
      const state = this.viewState();
      const storage = this.storage();
      if (!storage) return;

      storage.setItem(this.storageKeys.viewMode, state.viewModeId);
      if (state.sortById) {
        storage.setItem(this.storageKeys.sortBy, state.sortById);
      }
      if (state.pageSize !== undefined) {
        storage.setItem(this.storageKeys.pageSize, state.pageSize.toString());
      }
    });
  }

  ngOnInit(): void {
    this.loadStateFromStorage();
  }

  /**
   * Load state from storage
   */
  private loadStateFromStorage(): void {
    const storage = this.storage();
    if (!storage) return;

    const viewModeId = storage.getItem(this.storageKeys.viewMode) || this.defaultViewMode();
    const sortById = storage.getItem(this.storageKeys.sortBy) || this.defaultSortBy();
    const pageSizeStr = storage.getItem(this.storageKeys.pageSize);
    const pageSize = pageSizeStr ? Number.parseInt(pageSizeStr, 10) : this.defaultPageSize();

    this.viewState.set({
      viewModeId,
      sortById,
      pageSize,
    });
  }

  /**
   * Handle view mode change
   */
  onViewModeChange(viewModeId: unknown): void {
    this.viewState.update(state => ({
      ...state,
      viewModeId: String(viewModeId),
    }));
  }

  /**
   * Handle sort change
   */
  onSortChange(sortById: unknown): void {
    this.viewState.update(state => ({
      ...state,
      sortById: String(sortById),
    }));
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(pageSize: unknown): void {
    this.viewState.update(state => ({
      ...state,
      pageSize: Number.parseInt(String(pageSize), 10),
    }));
  }

  /**
   * Handle card click
   */
  onCardClick(item: unknown): void {
    this.cardClick.emit(item as T);
  }

  /**
   * Get data (sorting is handled by parent component via data input)
   * The sortById in viewState is used by parent to determine sorting
   */
  get sortedData(): T[] {
    return this.data();
  }
}
